import { getSupabaseClient } from '../../lib/supabase';
import { listTutorLearnerScopes } from '../insights/learnerInsightSupabase';
import { assertTutorSubjectScope } from '../insights/learnerInsightScope';

export type TutorNoteVisibility = 'staff_only' | 'guardian_shared';

export type CreateTutorNoteInput = {
  studentId: string;
  subjectId: string;
  body: string;
  visibility: TutorNoteVisibility;
};

export type CreatedTutorNote = {
  id: string;
  studentId: string;
  subjectId: string;
  visibility: TutorNoteVisibility;
  body: string;
  createdAt: string;
};

function localIsoDate(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export async function createTutorNote(input: CreateTutorNoteInput): Promise<CreatedTutorNote> {
  const body = input.body.trim();
  if (!body) throw new Error('Catatan belum diisi.');
  if (body.length > 2000) throw new Error('Catatan terlalu panjang. Maksimal 2000 karakter.');
  if (input.visibility !== 'staff_only' && input.visibility !== 'guardian_shared') {
    throw new Error('Visibilitas catatan Tutor tidak didukung.');
  }

  const client = getSupabaseClient();
  if (!client) throw new Error('Supabase belum dikonfigurasi.');

  const scopes = await listTutorLearnerScopes(localIsoDate());
  assertTutorSubjectScope(scopes, input.studentId, input.subjectId, localIsoDate());

  const authResult = await client.auth.getUser();
  if (authResult.error) throw authResult.error;
  if (!authResult.data.user) throw new Error('Sesi Tutor sudah berakhir. Silakan masuk kembali.');

  const result = await client
    .from('student_notes')
    .insert({
      student_id: input.studentId,
      subject_id: input.subjectId,
      author_profile_id: authResult.data.user.id,
      note_type: 'learning',
      visibility: input.visibility,
      body,
      status: 'active',
    })
    .select('id,student_id,subject_id,visibility,body,created_at')
    .single();

  if (result.error) throw result.error;

  return {
    id: String(result.data.id),
    studentId: String(result.data.student_id),
    subjectId: String(result.data.subject_id),
    visibility: result.data.visibility as TutorNoteVisibility,
    body: String(result.data.body),
    createdAt: String(result.data.created_at),
  };
}
